import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/apiConfig';

function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch orders from API
  const fetchOrders = async (page = 1, status = '') => {
    try {
      setLoading(true);
      setError(null);
      
      const token = sessionStorage.getItem('token');
      if (!token) {
        setError('Please login to view your orders');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      if (status) {
        params.append('status', status);
      }

      const response = await axios.get(`${API_BASE_URL}/product/orders/user?${params}`, {
        headers: {
          'Authorization': token
        }
      });
      
      if (response.data.is_success) {
        setOrders(response.data.data.orders);
        setPagination(response.data.data.pagination);
      } else {
        setError(response.data.error || 'Failed to fetch orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      if (err.response && err.response.status === 401) {
        setError('Session expired. Please login again.');
      } else {
        setError('Failed to load orders. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(currentPage, statusFilter);
  }, [currentPage, statusFilter]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleCheckLatestStatus = async (orderId) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        setError('Please login to check order status');
        return;
      }

      console.log('Checking latest status from gateway for order:', orderId);

      const response = await axios.get(`${API_BASE_URL}/product/order/${orderId}/gateway-status`, {
        headers: {
          'Authorization': token
        }
      });
      
      if (response.data.is_success) {
        // Update the order status in the local state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.order_id === orderId 
              ? { 
                  ...order, 
                  status: response.data.data.status,
                  payment_info: response.data.data.payment_info || order.payment_info
                }
              : order
          )
        );
        
        console.log('Order status updated successfully');
        
        // Show a brief success message
        const originalError = error;
        setError(`Status updated: ${response.data.data.status.toUpperCase()}`);
        setTimeout(() => {
          setError(originalError);
        }, 3000);
      } else {
        console.error('Failed to check status:', response.data);
        setError('Failed to check latest status. Please try again.');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      console.error('Error checking latest status:', err);
      setError('Error checking latest status. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const getStatusIcon = () => '●';

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <OrderHistoryContainer>
        <LoadingText>Loading your orders...</LoadingText>
      </OrderHistoryContainer>
    );
  }

  if (error) {
    return (
      <OrderHistoryContainer>
        <ErrorText>{error}</ErrorText>
        <RetryButton onClick={() => fetchOrders(currentPage, statusFilter)}>
          Try Again
        </RetryButton>
      </OrderHistoryContainer>
    );
  }

  return (
    <OrderHistoryContainer>
      <Header>
        <Title>Order History</Title>
        <FilterContainer>
          <FilterButton 
            active={statusFilter === ''} 
            onClick={() => handleStatusFilter('')}
          >
            All
          </FilterButton>
          <FilterButton 
            active={statusFilter === 'completed'} 
            onClick={() => handleStatusFilter('completed')}
          >
            Completed
          </FilterButton>
          <FilterButton 
            active={statusFilter === 'pending'} 
            onClick={() => handleStatusFilter('pending')}
          >
            Pending
          </FilterButton>
          <FilterButton 
            active={statusFilter === 'failed'} 
            onClick={() => handleStatusFilter('failed')}
          >
            Failed
          </FilterButton>
        </FilterContainer>
      </Header>

      {orders.length === 0 ? (
        <EmptyState>
          <EmptyIcon />
          <EmptyTitle>No Orders Found</EmptyTitle>
          <EmptyMessage>
            {statusFilter 
              ? `No ${statusFilter} orders found. Try changing the filter.`
              : "You haven't placed any orders yet. Start shopping to see your orders here!"
            }
          </EmptyMessage>
        </EmptyState>
      ) : (
        <>
          <OrdersList>
            {orders.map((order) => (
              <OrderCard key={order.order_id}>
                <OrderHeader>
                  <OrderId>Order #{order.order_id}</OrderId>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* <StatusBadge status={order.status}>
                      <StatusIcon>{getStatusIcon(order.status)}</StatusIcon>
                      {order.status?.toUpperCase()}
                    </StatusBadge> */}
                    <StatusBadge status={(order.order_status || 'order placed').toLowerCase() === 'order delivered' ? 'completed' : 'pending'}> 
                      <StatusIcon>●</StatusIcon>
                      {(order.order_status || 'order placed').toUpperCase()}
                    </StatusBadge>
                  </div>
                </OrderHeader>

                <OrderContent>
                  {order.product && (
                    <ProductInfo>
                      <ProductImage 
                        src={order.product.images && order.product.images[0] 
                          ? order.product.images[0] 
                          : '/default-product.png'
                        } 
                        alt={order.product.name}
                        onError={(e) => {
                          e.target.src = '/default-product.png';
                        }}
                      />
                      <ProductDetails>
                        <ProductName>{order.product.name}</ProductName>
                        <ProductMeta>
                          {order.product.brand && <span>{order.product.brand}</span>}
                          {order.product.category && <span> • {order.product.category}</span>}
                        </ProductMeta>
                      </ProductDetails>
                    </ProductInfo>
                  )}

                  <OrderMeta>
                    {/* <MetaRow>
                      <MetaLabel>Order Status:</MetaLabel>
                      <MetaValue>{(order.order_status || 'order placed').replace(/\b\w/g, c => c.toUpperCase())}</MetaValue>
                    </MetaRow> */}
                    <MetaRow>
                      <MetaLabel>Amount:</MetaLabel>
                      <MetaValue>₹{parseFloat(order.amount).toLocaleString('en-IN')}</MetaValue>
                    </MetaRow>
                    <MetaRow>
                      <MetaLabel>Payment Status:</MetaLabel>
                      <MetaValue>{order.status?.toUpperCase()}</MetaValue>
                    </MetaRow>
                    <MetaRow>
                      <MetaLabel>Order Date:</MetaLabel>
                      <MetaValue>{formatDate(order.created_at)}</MetaValue>
                    </MetaRow>
                    {order.payment_info && order.payment_info.transaction_id && (
                      <MetaRow>
                        <MetaLabel>Transaction ID:</MetaLabel>
                        <MetaValue>{order.payment_info.transaction_id}</MetaValue>
                      </MetaRow>
                    )}
                    {order.shipping_address && (
                      <MetaRow>
                        <MetaLabel>Delivery to:</MetaLabel>
                        <MetaValue>
                          {order.shipping_address.name}, {order.shipping_address.city}
                        </MetaValue>
                      </MetaRow>
                    )}
                  </OrderMeta>
                </OrderContent>

                <OrderActions>
                  <ActionButton 
                    onClick={() => {
                      // Store order info in session storage and navigate to order page
                      sessionStorage.setItem('current_order_id', order.order_id);
                      sessionStorage.setItem('order_type', 'product');
                      window.open(`/order?order_id=${order.order_id}`, '_blank');
                    }}
                  >
                    View Details
                  </ActionButton>
                  {(order.status === 'pending' /* || order.status === 'failed' */) && (
                    <ActionButton 
                      primary
                      onClick={() => handleCheckLatestStatus(order.order_id)}
                    >
                      Check Latest Status
                    </ActionButton>
                  )}
                </OrderActions>
              </OrderCard>
            ))}
          </OrdersList>

          {pagination.total_pages > 1 && (
            <PaginationContainer>
              <PaginationButton 
                disabled={!pagination.has_prev}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </PaginationButton>
              
              <PageInfo>
                Page {pagination.current_page} of {pagination.total_pages}
              </PageInfo>
              
              <PaginationButton 
                disabled={!pagination.has_next}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </PaginationButton>
            </PaginationContainer>
          )}
        </>
      )}
    </OrderHistoryContainer>
  );
}

// Styled Components
const OrderHistoryContainer = styled.div`
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
`;

const Title = styled.h2`
  font: 800 24px Inter, sans-serif;
  letter-spacing: -0.48px;
  color: #202023;
  margin: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #202023;
  background-color: ${props => props.active ? '#e9fe53' : '#fff'};
  color: #202023;
  border-radius: 1px;
  cursor: pointer;
  font-family: Metrophobic, sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.active ? '#e9fe53' : '#f6f5f8'};
  }
`;

const LoadingText = styled.p`
  text-align: center;
  font: 400 16px Metrophobic, sans-serif;
  color: #666;
  margin-top: 50px;
`;

const ErrorText = styled.p`
  text-align: center;
  font: 400 16px Metrophobic, sans-serif;
  color: #dc3545;
  margin-top: 50px;
`;

const RetryButton = styled.button`
  display: block;
  margin: 20px auto;
  padding: 10px 20px;
  background-color: #e9fe53;
  color: #202023;
  border: 1px solid #202023;
  border-radius: 1px;
  cursor: pointer;
  font-family: Metrophobic, sans-serif;
  text-transform: uppercase;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  font: 600 20px Inter, sans-serif;
  color: #202023;
  margin-bottom: 10px;
`;

const EmptyMessage = styled.p`
  font: 400 16px Metrophobic, sans-serif;
  color: #666;
  line-height: 1.5;
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const OrderCard = styled.div`
  border: 1px solid #202023;
  border-radius: 1px;
  padding: 20px;
  background-color: #f6f5f8;
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
`;

const OrderId = styled.h3`
  font: 600 16px Inter, sans-serif;
  color: #202023;
  margin: 0;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 5px;
  background-color: ${props => getStatusColor(props.status)}20;
  color: ${props => getStatusColor(props.status)};
  font: 600 12px Inter, sans-serif;
  text-transform: uppercase;
`;

const StatusIcon = styled.span`
  font-size: 14px;
`;

const OrderContent = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  gap: 15px;
  flex: 1;
  min-width: 0;
`;

const ProductImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 1px;
  //border: 1px solid #202023;
  background-color: #fff;
`;

const ProductDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled.h4`
  font: 600 14px Inter, sans-serif;
  color: #202023;
  margin: 0 0 5px 0;
  line-height: 1.3;
`;

const ProductMeta = styled.p`
  font: 400 12px Metrophobic, sans-serif;
  color: #666;
  margin: 0;
`;

const OrderMeta = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MetaLabel = styled.span`
  font: 500 12px Inter, sans-serif;
  color: #666;
`;

const MetaValue = styled.span`
  font: 400 12px Metrophobic, sans-serif;
  color: #202023;
  text-align: right;
`;

const OrderActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #202023;
  background-color: ${props => props.primary ? '#e9fe53' : 'transparent'};
  color: #202023;
  text-decoration: none;
  border-radius: 1px;
  cursor: pointer;
  font-family: Metrophobic, sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  transition: background-color 0.2s ease;
  display: inline-block;
  text-align: center;

  &:hover {
    background-color: ${props => props.primary ? '#e9fe53' : '#f6f5f8'};
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
`;

const PaginationButton = styled.button`
  padding: 10px 20px;
  border: 1px solid #202023;
  background-color: #fff;
  color: #202023;
  border-radius: 1px;
  cursor: pointer;
  font-family: Metrophobic, sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #f6f5f8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  font: 500 14px Inter, sans-serif;
  color: #666;
`;

// Helper function for status color (moved outside component for better performance)
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'success':
      return '#28a745';
    case 'pending':
      return '#ffc107';
    case 'failed':
    case 'cancelled':
      return '#dc3545';
    default:
      return '#6c757d';
  }
};

export default OrderHistory; 